import { useNavigation } from "@react-navigation/native";
import React, { memo, useContext, useEffect, useState } from "react";
import { StyleSheet, TouchableNativeFeedback, View } from "react-native";
import Image from "@components/common/Image";
import { useDispatch, useSelector } from "react-redux";
import CheckGreen from "@assets/svg/CheckGreen";
import DownArrowSmall from "@assets/svg/DownArrowSmall";
import Text from "@components/AppText";
import useLeaderState from "@hooks/useLeaderState";
import { actions as productActions } from "@reducers/productReducer";
import { actions } from "@reducers/qtySelectReducer";
import { CartContext } from "@screens/PlaceOrder/PlaceOrderView";
import { notifyForOOS } from "@services/api";
import {
  ORDER_FLOW_EVENTS,
  SHARING_EVENTS,
} from "../../constants/analyticsEventsCT";
import parseTemplate from "../../utils/parseTemplate";
import { shareOnWhatsapp } from "../../utils/shareEntity";
import useCartActions from "../CMS/useCartActions";
import AddnMinBtn from "./AddnMinBtn";
import CommissionStrip from "./CommissionStrip";
import DiscountStrip from "./DiscountStrip";
import KhareedBtn from "./KhareedBtn";
import { normalize, width } from "./metrics";
import NotifyButton from "./NotifyButton";
import ShareOnWA from "./ShareOnWA";

const formatPrice = (price) => {
  return price.split(".");
};
const Product = memo((props) => {
  const {
    data,
    index,
    defaultShare,
    routeName = "",
    trackEvent = () => {},
    skuListData = {},
    horizontal = false,
    navigateMethod = "navigate",
    sharingTemplate = "",
    disabled,
    sharingLink = "",
  } = props;

  const { children, variant_identifier } = data;
  const [notified, setNotified] = useState(false);
  const [firstRender, setFirstRender] = useState(true);
  const cartConsumer = useContext(CartContext);
  const dispatch = useDispatch();
  const navigation = useNavigation();

  const { identifier, displayId } = useSelector((state) => state.qtySelection);
  const [displayItem, setDisplayItem] = useState(
    Array.isArray(children) && children.find((el) => el.sku_id === data.sku_id),
  );

  useEffect(() => {
    setFirstRender(false);
  }, []);

  useEffect(() => {
    if (identifier === variant_identifier && displayId !== "") {
      setDisplayItemHandler(displayId);
    }
  });

  const setDisplayItemHandler = (id) => {
    const newItem = children.find((el) => el.sku_id === id);
    setDisplayItem(newItem);
  };

  const {
    discount,
    discount_type,
    short_name_hi,
    product_image,
    price_tl_single_buy,
    price_per_unit,
    mrp,
    grammage,
    custom_text,
    custom_symbol,
    product_status,
    slug,
    sku_id,
    product_name_hi,
    images = [],
    price_tl_commission,
    quantity,
  } = displayItem || data;

  const { addCart, removeCart, countOfItem, isCartValueLoading } =
    useCartActions();

  const discount_price = mrp - price_tl_single_buy;

  const itemCount = quantity || countOfItem(sku_id);

  const { canLeaderUseApp } = useLeaderState();

  useEffect(() => {
    if (
      typeof cartConsumer !== "undefined" &&
      typeof cartConsumer.updateCart === "function" &&
      firstRender === false
    ) {
      cartConsumer.updateCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCount]);

  const dataObj = {
    mrp,
    price_tl_single_buy,
    product_name_hi,
    discount_price,
    leaderName: props.leaderName,
    leaderId: props.leaderId,
    slug,
  };

  const { selectedContact, cart } = useSelector((state) => ({
    selectedContact: state.customer.selectedContact,
  }));

  const numOfItem = countOfItem(sku_id);
  const [showCartActions, setShowCartActions] = useState(false);

  const onPressPlus = () => {
    if (!canLeaderUseApp()) {
      return;
    }
    addCart(
      sku_id,
      selectedContact.user_id,
      () => {},
      countOfItem(sku_id) + 1,
      {
        sku_id,
        price_tl_single_buy,
        product_name_hi,
        position: index,
      },
    );
  };

  const onPressMinus = () => {
    if (!canLeaderUseApp()) {
      return;
    }
    removeCart(
      sku_id,
      selectedContact.user_id,
      () => {},
      countOfItem(sku_id) - 1,
      {
        sku_id,
        price_tl_single_buy,
        product_name_hi,
        position: index,
      },
    );
  };

  const notifyOOS = () => {
    setNotified(true);
    notifyForOOS({ sku_id })
      .then(() => {
        dispatch(productActions.requestForOosSkus());
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const oosProducts = useSelector((state) => state.products.oosProducts) || [];

  const isOutOfStockNotified =
    oosProducts.findIndex((p) => p.sku_id === sku_id) !== -1;

  useEffect(() => {
    setNotified(isOutOfStockNotified);
  }, [isOutOfStockNotified, oosProducts]);

  return (
    <TouchableNativeFeedback
      disabled={disabled}
      onPress={() => {
        navigation[navigateMethod]("ProductDetails", {
          slug,
          fromScreen: routeName,
          cmsData: skuListData,
        });
        trackEvent(ORDER_FLOW_EVENTS.PRODUCT_CLICKED, {
          sku_id,
          product_price: price_tl_single_buy,
          product_name: product_name_hi,
          position: index,
        });
      }}
    >
      <View
        style={{
          borderColor: "#EEEEEE",
          borderWidth: 1,
          width: width * 0.5,
          position: "relative",
          paddingBottom: normalize(4),
          paddingTop: horizontal ? normalize(15) : normalize(10),
          marginRight: horizontal ? normalize(15) : 0,
          backgroundColor: "white",
        }}
      >
        <DiscountStrip discount_type={discount_type} discount={discount} />
        <Image
          resizeMode={Image.resizeMode.contain}
          style={{
            alignSelf: "center",
            width: normalize(100),
            height: normalize(100),
            opacity: product_status === "Active" ? 1 : 0.2,
          }}
          source={
            product_image
              ? {
                  uri: product_image,
                }
              : require("@assets/images/defaultImage.webp")
          }
        />
        <View
          style={{
            paddingHorizontal: normalize(10),
            marginTop: normalize(10),
          }}
        >
          <Text numberOfLines={1} ellipsizeMode="tail">
            {product_name_hi}
          </Text>
        </View>
        {product_status === "Active" ? (
          <View
            style={{
              flexDirection: "row",
              marginTop: normalize(5),
              paddingHorizontal: normalize(10),
            }}
          >
            <View
              style={{
                alignItems: "center",
                paddingBottom: 2,
              }}
            >
              <Text bold style={{ fontSize: normalize(16) }}>
                ₹{formatPrice(price_tl_single_buy)[0]}
                {formatPrice(price_tl_single_buy)[1] && (
                  <Text bold style={{ fontSize: normalize(12) }}>
                    .{formatPrice(price_tl_single_buy)[1]}
                  </Text>
                )}
              </Text>
            </View>
            <View
              style={{
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {parseFloat(mrp) !== parseFloat(price_tl_single_buy) && (
                <Text
                  style={{
                    ...styles.mrp,
                    marginRight: normalize(10),
                    textDecorationLine: "line-through",
                  }}
                >
                  ₹{mrp}
                </Text>
              )}
            </View>
            <View
              style={{
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
              }}
            >
              {price_per_unit ? (
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  style={{
                    color: "rgba(0, 0, 0, 0.56)",
                    fontSize: normalize(12),
                    lineHeight: normalize(16),
                  }}
                >
                  ({price_per_unit})
                </Text>
              ) : (
                <></>
              )}
            </View>
          </View>
        ) : (
          <View
            style={{
              marginTop: normalize(5),
              paddingHorizontal: normalize(10),
            }}
          >
            <Text style={{ color: "#FF2E00", fontSize: normalize(16) }}>
              यह प्रोडक्ट स्टॉक में नहीं है
            </Text>
          </View>
        )}

        {product_status === "Active" && price_tl_commission ? (
          <View
            style={{
              paddingHorizontal: 10,
              marginTop: 5,
              marginBottom: normalize(15),
            }}
          >
            <CommissionStrip
              {...{
                commission: price_tl_commission,
              }}
            />
          </View>
        ) : (
          <></>
        )}
        <View
          style={{
            flexDirection: "row",
            flex: 1,
          }}
        >
          <View
            style={{
              alignItems: "center",
              marginBottom: normalize(15),
              flex: 1,
            }}
          >
            {grammage && product_status === "Active" ? (
              <TouchableNativeFeedback
                disabled={children.length <= 1}
                onPress={() => {
                  dispatch(
                    actions.showBottomSheet({
                      items: children,
                      identifier: variant_identifier,
                    }),
                  );
                }}
              >
                <View
                  style={{
                    backgroundColor:
                      children.length <= 1 ? "rgba(0, 0, 0, 0.1)" : "white",
                    paddingHorizontal: normalize(10),
                    borderWidth: children.length <= 1 ? 0 : 1,
                    borderColor: "rgba(0, 0, 0, 0.2)",
                    borderRadius: 6,
                    height: normalize(30),
                    width: "90%",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text style={{ fontSize: normalize(14) }}>{grammage}</Text>
                  {children.length <= 1 ? null : <DownArrowSmall />}
                </View>
              </TouchableNativeFeedback>
            ) : (
              <></>
            )}
          </View>
          {product_status === "Active" ? (
            <View
              style={{
                alignItems: "center",
                flexDirection: "row",
                paddingRight: normalize(8),
              }}
            >
              <ShareOnWA
                width={normalize(40)}
                onPress={() => {
                  if (!canLeaderUseApp()) {
                    return;
                  }
                  const message = parseTemplate(sharingTemplate, dataObj, "_");

                  trackEvent(SHARING_EVENTS.PRODUCT_SHARE, {
                    sku_id,
                    price_tl_single_buy,
                    product_name: product_name_hi,
                    position: index,
                  });
                  shareOnWhatsapp({
                    message: `${message}\n${sharingLink}`,
                    image: product_image,
                  });
                }}
                showCTA={false}
              />

              {/* <View
                style={{
                  flex: 1,
                  alignItems: "flex-end",
                }}>
                {selectedContact && (numOfItem || showCartActions) ? (
                  <AddnMinBtn
                    width={normalize(100)}
                    onPressPlus={() => onPressPlus()}
                    onPressMinus={() => onPressMinus()}
                    isCartValueLoading={isCartValueLoading}
                    count={countOfItem(sku_id)}
                  />
                ) : (
                  <KhareedBtn
                    width={normalize(100)}
                    onPress={() => {
                      if (!canLeaderUseApp()) {
                        return;
                      }
                      if (selectedContact) {
                        addCart(
                          sku_id,
                          selectedContact.user_id,
                          () => {
                            if (countOfItem(sku_id)) {
                              setShowCartActions(true);
                            }
                          },
                          1,
                          {
                            sku_id,
                            price_tl_single_buy,
                            product_name_hi,
                            position: index,
                          },
                        );
                      } else {
                        navigation.navigate("Customer", {
                          sku_id,
                          price_tl_single_buy,
                          product_name_hi,
                          position: index,
                          screen_name: routeName,
                        });
                      }
                    }}
                  />
                )}
              </View> */}
            </View>
          ) : (
            <View style={{ marginTop: "auto", marginBottom: normalize(15) }}>
              {!notified ? (
                <NotifyButton
                  width="89%"
                  style={{ alignSelf: "center" }}
                  onPress={notifyOOS}
                />
              ) : (
                <View
                  style={{
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                    borderRadius: 6,
                    paddingHorizontal: 5,
                    flexDirection: "row",
                    alignItems: "center",

                    alignSelf: "center",
                    height: normalize(30),
                  }}
                >
                  <CheckGreen />
                  <Text
                    style={{
                      color: "rgba(0, 0, 0, 0.56)",
                      fontSize: normalize(14),
                      marginLeft: normalize(2),
                    }}
                  >
                    आपको सूचित किया जायेगा
                  </Text>
                </View>
              )}
            </View>
          )}
        </View>

        {custom_symbol && custom_text ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              marginBottom: normalize(10),
              width: "90%",
              alignSelf: "center",
            }}
          >
            <View style={{ paddingTop: normalize(2) }}>
              <Image
                resizeMode={Image.resizeMode.contain}
                source={{
                  uri: custom_symbol,
                }}
                style={{ height: normalize(20), width: normalize(20) }}
              />
            </View>
            <View
              style={{
                paddingHorizontal: normalize(5),
                flex: 1,
              }}
            >
              <Text
                bold
                style={{
                  color: "rgba(0, 0, 0, 0.56)",
                  fontSize: normalize(12),
                }}
                numberOfLines={2}
                ellipsizeMode="tail"
              >
                {custom_text}
              </Text>
            </View>
          </View>
        ) : (
          <></>
        )}
      </View>
    </TouchableNativeFeedback>
  );
});

export default Product;

const styles = StyleSheet.create({
  mrp: {
    fontSize: normalize(12),
    marginLeft: normalize(10),
  },
});
